/**
 * Matching and ranking for the command palette: umlaut- and accent-blind,
 * word starts before substrings, initials ("nr" → „Neue Rechnung“) and a
 * loose subsequence as the last resort. Pure functions — the palette and its
 * tests share them. The fold is the shared one of `lib/textQuery`; the ranking
 * stays the palette's own.
 */
import { fold as foldText, type Folded } from '../lib/textQuery';

/** A half-open range [start, end) into the original text. */
export type MatchRange = [number, number];

export interface TextMatch {
  /** Higher is better; 0 never occurs (no match returns null). */
  score: number;
  /** Ranges of the original text that matched, for highlighting. */
  ranges: MatchRange[];
}

/** A folded text with `origin` — `from` under the name the palette always used. */
interface PaletteFolded extends Folded {
  /** For every folded character, where its original character starts. */
  origin: number[];
}

/**
 * Folds text for matching: lower case, umlauts spelled out (ä → ae), other
 * accents dropped — the fold of `lib/textQuery`, which also maps decomposed
 * letters. `from` / `to` map every folded character back to the original one
 * so matches can be highlighted in the original text; `origin` is `from`, kept
 * for callers that read it.
 */
export function fold(text: string): PaletteFolded {
  const folded = foldText(text);
  return { ...folded, origin: folded.from };
}

/** Folds a query the same way and splits it into tokens. */
export function tokens(query: string): string[] {
  return foldText(query.trim()).text.split(/\s+/).filter(Boolean);
}

const isWordChar = (ch: string | undefined) => !!ch && /[\p{L}\p{N}]/u.test(ch);

function wordStarts(folded: string): number[] {
  const starts: number[] = [];
  for (let i = 0; i < folded.length; i++) {
    if (isWordChar(folded[i]) && !isWordChar(folded[i - 1])) starts.push(i);
  }
  return starts;
}

function toRanges(f: Folded, positions: number[]): MatchRange[] {
  const ranges: MatchRange[] = [];
  for (const p of positions) {
    const from = f.from[p];
    const to = f.to[p];
    const last = ranges[ranges.length - 1];
    if (last && from <= last[1]) last[1] = Math.max(last[1], to);
    else ranges.push([from, to]);
  }
  return ranges;
}

const span = (from: number, length: number) => Array.from({ length }, (_, i) => from + i);

/** Best match of one folded token in the folded text, or null. */
function matchToken(f: Folded, starts: number[], token: string): TextMatch | null {
  const text = f.text;
  if (text === token) return { score: 100, ranges: toRanges(f, span(0, token.length)) };
  if (text.startsWith(token)) return { score: 90, ranges: toRanges(f, span(0, token.length)) };
  for (const s of starts) {
    if (text.startsWith(token, s)) return { score: 75, ranges: toRanges(f, span(s, token.length)) };
  }
  // Initials: every token character starts a word, in order ("nr").
  if (token.length >= 2 && token.length <= starts.length) {
    const hit: number[] = [];
    let w = 0;
    for (const ch of token) {
      while (w < starts.length && text[starts[w]] !== ch) w++;
      if (w === starts.length) break;
      hit.push(starts[w++]);
    }
    if (hit.length === token.length) return { score: 65, ranges: toRanges(f, hit) };
  }
  const at = text.indexOf(token);
  if (at >= 0) return { score: 50, ranges: toRanges(f, span(at, token.length)) };
  // Subsequence starting at a word start, with few gaps, only for tokens long
  // enough to mean it („estl“ → „Einstellungen“, but „neu“ ↛ „Einstellungen“).
  if (token.length >= 3) {
    for (const s of starts) {
      if (text[s] !== token[0]) continue;
      const hit = [s];
      let from = s + 1;
      for (const ch of token.slice(1)) {
        const i = text.indexOf(ch, from);
        if (i < 0) break;
        hit.push(i);
        from = i + 1;
      }
      if (hit.length !== token.length) continue;
      const spread = hit[hit.length - 1] - hit[0] + 1 - token.length;
      if (spread > token.length) continue;
      return { score: Math.max(10, 35 - spread * 3), ranges: toRanges(f, hit) };
    }
  }
  return null;
}

/**
 * Matches all query tokens against text. Every token has to match; the score
 * is the mean of the token scores.
 */
export function matchText(text: string, queryTokens: string[]): TextMatch | null {
  if (!queryTokens.length) return { score: 0, ranges: [] };
  const f = foldText(text);
  const starts = wordStarts(f.text);
  let score = 0;
  const ranges: MatchRange[] = [];
  for (const token of queryTokens) {
    const m = matchToken(f, starts, token);
    if (!m) return null;
    score += m.score;
    ranges.push(...m.ranges);
  }
  ranges.sort((a, b) => a[0] - b[0]);
  return { score: score / queryTokens.length, ranges: mergeRanges(ranges) };
}

function mergeRanges(ranges: MatchRange[]): MatchRange[] {
  const out: MatchRange[] = [];
  for (const r of ranges) {
    const last = out[out.length - 1];
    if (last && r[0] <= last[1]) last[1] = Math.max(last[1], r[1]);
    else out.push([r[0], r[1]]);
  }
  return out;
}

/** A searchable field of an entry and how much it counts. */
export interface Field {
  text: string | undefined;
  weight: number;
}

/**
 * Scores an entry over its fields: every token must match in some field; a
 * token counts with its best field. Ranges are reported for the first field
 * (the label) only.
 */
export function matchFields(fields: Field[], queryTokens: string[]): TextMatch | null {
  if (!queryTokens.length) return { score: 0, ranges: [] };
  const prepared = fields
    .filter((f): f is { text: string; weight: number } => !!f.text)
    .map((f) => {
      const folded = foldText(f.text);
      return { ...f, folded, starts: wordStarts(folded.text) };
    });
  const label = fields[0]?.text ? prepared[0] : undefined;
  let total = 0;
  const ranges: MatchRange[] = [];
  for (const token of queryTokens) {
    let best = 0;
    let bestRanges: MatchRange[] | null = null;
    for (const field of prepared) {
      const m = matchToken(field.folded, field.starts, token);
      if (!m) continue;
      const weighted = m.score * field.weight;
      if (weighted > best) {
        best = weighted;
        bestRanges = field === label ? m.ranges : null;
      }
    }
    if (!best) return null;
    total += best;
    if (bestRanges) ranges.push(...bestRanges);
  }
  ranges.sort((a, b) => a[0] - b[0]);
  return { score: total / queryTokens.length, ranges: mergeRanges(ranges) };
}

/** Splits text into plain and marked pieces along ranges. */
export function highlight(text: string, ranges: MatchRange[]): { text: string; mark: boolean }[] {
  const parts: { text: string; mark: boolean }[] = [];
  let at = 0;
  for (const [s, e] of ranges) {
    if (s > at) parts.push({ text: text.slice(at, s), mark: false });
    parts.push({ text: text.slice(s, e), mark: true });
    at = e;
  }
  if (at < text.length) parts.push({ text: text.slice(at), mark: false });
  return parts;
}

/**
 * Marks every occurrence of the query tokens in text (for snippets that came
 * from a server, where no ranges exist).
 */
export function markTokens(text: string, queryTokens: string[]): MatchRange[] {
  const f = foldText(text);
  const positions: number[] = [];
  for (const token of queryTokens) {
    if (!token) continue;
    let from = 0;
    for (;;) {
      const at = f.text.indexOf(token, from);
      if (at < 0) break;
      positions.push(...span(at, token.length));
      from = at + token.length;
    }
  }
  positions.sort((a, b) => a - b);
  return toRanges(f, [...new Set(positions)]);
}
